import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";

const COMPANY_NAME = "NetBasis International";

const title = "Project Management";
const heroImage = "https://net-basis.net/Bilder/project-management-planning-control-dach-market-entry.webp";
const heroAlt = "Businessman creates a flowchart for secure project management during market entry.";
const heroClaimLine1 = "Perfectly planned!";
const heroClaimLine2 = "Safely managed!";
const widget2 = "Your experienced partner for comprehensive project consulting and direct project management in the DACH region. We guide complex market entry projects for foreign manufacturers with a goal-oriented, risk-conscious approach and deep local expertise.";
const benefitsTitle = "How you benefit from our __Project Management__ expertise:";
const p1 = "NetBasis International supports its manufacturing partners in their market entry, upon request, with comprehensive project consulting and direct project management. After all, the plan to establish a presence in new markets is, in most cases, a strategic and therefore highly important decision. And failure must be avoided at all costs. At the same time, such an endeavor is often extremely complex and demands a higher degree of market-specific expertise, professional competence, and targeted focus than is typically available to a company that is not present on-site.";
const bottomClaim = "NetBasis International - drive to excellence with excellent drivers !";

const benefits = [
  "Reliable assumption of planning, monitoring, and steering a market entry project",
  "Structured assistance in aligning project scope, deadlines, costs, financing, and necessary resources",
  "Professional execution of all local project controls on-site",
  "Sound support in the identification, analysis, evaluation, and management of project risks",
  "Continuous observation of project progress and precise comparison with the project planning",
  "Seamless monitoring of adherence to deadlines, content progress, and budget compliance",
  "Permanent project information and transparent communication with the manufacturing partner",
  "Competent assistance with all country-specific and market-specific legal and regulatory affairs",
];

export default function ProjectManagementEnPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <PageHead
        title="Project Management | NetBasis International"
        description="Efficient steering and operational implementation of your market entry and sales projects by experienced industry experts."
        ogUrl="https://net-basis.net/en/services/business-development/project-management"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        {/* Hero image */}
        <section className="w-full bg-white py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="relative w-full h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
              <img loading="lazy"
                src={heroImage}
                alt={heroAlt}
                title={title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>

              <div className="absolute inset-0 z-20 flex items-start justify-start pt-[22%] md:pt-[10%] lg:pt-[12%] pl-[8%] md:pl-[10%] pointer-events-none">
                <p
                  className="text-3xl md:text-4xl lg:text-5xl font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-center leading-[1.15] md:leading-[1.5] animate-[trainingTextSlide_12s_ease-in-out_infinite]"
                  style={{
                    fontFamily: '"Open Sans", sans-serif',
                  }}
                >
                  <span className="block">{heroClaimLine1}</span>
                  {heroClaimLine2 && <span className="block">{heroClaimLine2}</span>}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {title}
            </h2>

            <div className="space-y-4 text-foreground-950 leading-relaxed text-sm md:text-base">
              <h1 className="text-sm md:text-base font-semibold text-foreground-950 mb-4">{widget2}</h1>
              <p>
                {(() => {
                  const parts = p1.split(COMPANY_NAME);
                  return parts.map((part, i) => (
                    <span key={i}>
                      {part}
                      {i < parts.length - 1 && (
                        <strong className="text-primary-600">{COMPANY_NAME}</strong>
                      )}
                    </span>
                  ));
                })()}
              </p>
            </div>

            {/* Green heading with italic word */}
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mt-8 md:mt-10 mb-5 md:mb-7">
              {benefitsTitle.split("__").map((part, i) =>
                i % 2 === 1 ? <em key={i}>{part}</em> : part
              )}
            </h2>

            {/* Benefits with green checkmarks */}
            <div className="space-y-1.5 md:space-y-2 text-foreground-950 leading-relaxed text-sm md:text-base">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-2 md:gap-3">
                  <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-primary-600 text-base md:text-lg"></i>
                  </div>
                  <p>{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom claim */}
        <section className="py-6 md:py-10 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <p className="font-heading text-lg md:text-xl font-bold text-primary-600">
              {bottomClaim}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}